import React, { FC, useEffect, useMemo, useState } from "react";
import {
    useExportUpdateVodContentGroup,
    useGetSeriesContentGroups,
} from "../../../../../services/vod/Query";
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { ContentGroup } from "../../../../../models/vod/Models";
import SearchTextInput from "../../../../../components/themed/SearchTextInput";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronDown,
    faChevronLeft,
    faChevronRight,
    faChevronUp,
    faEye,
    faPenToSquare,
    faUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router";
import ContentGroupModal from "../form/ContentGroupModal";
import ExportModal from "../modal/ExportModal";

const VODSeriesContentGroupListingView: FC = () => {
    const navigate = useNavigate();
    const [ selectedRow, setSelectedRow ] = useState<ContentGroup | undefined>();

    const [ searchText, setSearchText ] = useState("");
    const [ debouncedSearch, setDebouncedSearch ] = useState("");
    const [ sorting, setSorting ] = React.useState([ { id: "lastModifiedDateTime", desc: true } ]);
    const [ pagination, setPagination ] = React.useState({ pageIndex: 0, pageSize: 20 });

    const exportUpdateVodContentGroup = useExportUpdateVodContentGroup();

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchText.trim()), 350);
        return () => clearTimeout(t);
    }, [ searchText ]);

    const { data: contentGroupEntityCollection, isLoading, isFetching } =
        useGetSeriesContentGroups({
            search: debouncedSearch,
            pageIndex: pagination.pageIndex,
            pageSize: pagination.pageSize,
            sortField: sorting[0].id,
            sortDirection: sorting[0].desc ? "DESC" : "ASC",
        });

    const buildNavLink =
        (contentGroup: ContentGroup) => `/dashboard/vod/assets/series-content-group/${contentGroup.id}`;

    const columns: ColumnDef<ContentGroup>[] = useMemo(
        () => [
            {
                id: "id",
                header: "ID",
                accessorKey: "id",
                size: 70,
            },
            {
                id: "providerName",
                header: "Aggregator",
                accessorKey: "providerName",
                size: 120,
            },
            {
                id :"contentPartnerName",
                header: "Content Partner",
                accessorKey: "contentPartnerName",
                size: 120,
            },
            {
                id: "title",
                header: "Title",
                accessorKey: "title",
            },
            {
                id: "lastModifiedDateTime",
                header: "Last Modified",
                accessorFn: d => dayjs(d.lastModifiedDateTime).format("YYYY-MM-DD HH:mm:ss"),
                size: 100,
            },
            {
                id: "row-action-buttons",
                cell: ({ cell, row }) => (
                    <div key={`${cell.id}-${row.id}`}>
                        <button
                            data-tooltip-content="Edit Series Main Metadata"
                            data-tooltip-id="global-tooltip"
                            command="show-modal" commandfor="edit-vod-content-group-main-metadata-listing"
                            onClick={(e) => {
                                setSelectedRow(row.original);
                                e.stopPropagation();
                            }}
                        >
                            <FontAwesomeIcon icon={faPenToSquare} size="1x" className="text-brand-300 ml-4"/>
                        </button>
                        <button
                            data-tooltip-content="Synamedia Export"
                            data-tooltip-id="global-tooltip"
                            command="show-modal" commandfor="export-update-vod-content-group-listing"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedRow(row.original);
                            }}
                        >
                            <FontAwesomeIcon icon={faUpRightFromSquare} size="1x" className="text-brand-300 ml-4"/>
                        </button>
                        <button
                            data-tooltip-content="View Series Main Metadata"
                            data-tooltip-id="global-tooltip"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(buildNavLink(row.original));
                            }}
                        >
                            <FontAwesomeIcon icon={faEye} size="1x" className="text-brand-300 ml-4"/>
                        </button>
                    </div>
                ),
                size: 80,
            },
        ],
        [ navigate ]
    );

    // Create the table and pass your options
    const table = useReactTable({
        columns,
        data: contentGroupEntityCollection?.items ?? [],
        state: {
            sorting,
            pagination,
        },
        manualSorting: true,
        manualFiltering: true,
        manualPagination: true,
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    return (
        <>
            <div className="flex flex-col gap-3 h-full min-h-0 p-4 max-w-12xl mx-auto">
                <div className="flex flex-row gap-3 items-center justify-between">
                    <div className="flex-1 min-w-0">
                        <h2>Series Content Groups</h2>
                    </div>
                    <div>
                        <SearchTextInput
                            value={searchText}
                            placeholder="Search"
                            handleUpdate={setSearchText}
                        />
                    </div>
                </div>

                <div className="flex-1 min-h-0">
                    <div className="h-full overflow-y-auto">
                        <table className="w-full table-fixed">
                            <thead className="sticky top-0 z-10 text-white dark:text-white forced-colors:text-[HighlightText] bg-gray-800 dark:bg-gray-800 forced-colors:bg-[Highlight]">
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <th key={header.id} className="sticky top-0 z-10 border-b" style={{ width: `${header.getSize()}px` }}>
                                            {header.isPlaceholder ? null : (
                                                <div
                                                    role="button"
                                                    onClick={() => header.column.toggleSorting(header.column.getIsSorted() === "asc")}
                                                    style={{ cursor: "pointer", userSelect: "none" }}
                                                    className={
                                                        header.column.getIsSorted()
                                                        ? (header.column.getIsSorted() === "asc"
                                                          ? "px-4 py-3 text-left border-t-2 border-brand-500 dark:border-brand-400"
                                                          : "px-4 py-3 text-left border-b-2 border-brand-500 dark:border-brand-400")
                                                        : "px-4 py-3 text-left border-0"}
                                                >
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                    {header.column.getIsSorted()
                                                        ? (header.column.getIsSorted() === "asc"
                                                            ? <FontAwesomeIcon icon={faChevronUp} size="1x" className="ml-4" />
                                                            : <FontAwesomeIcon icon={faChevronDown} size="1x" className="ml-4" />)
                                                        : <></>}
                                                </div>
                                            )}
                                        </th>
                                    ))}
                                </tr>
                            ))}
                            </thead>
                            <tbody>
                            {isFetching ? (
                                <tr>
                                    <td colSpan={columns.length} className="p-4 text-center">
                                        Fetching...
                                    </td>
                                </tr>
                            ) : isLoading ? (
                                <tr>
                                    <td colSpan={columns.length} className="p-4 text-center">
                                        Loading...
                                    </td>
                                </tr>
                            ) : contentGroupEntityCollection?.items?.length ? (
                                table.getRowModel().rows.map((row) => (
                                    <tr key={row.id} className="border-t hover:bg-brand-400 hover:text-white cursor-pointer font-mono" role="link" onClick={() => navigate(buildNavLink(row.original))}>
                                        {row.getVisibleCells().map((cell) => (
                                            <td key={cell.id} className="p-2 align-top" style={{ width: `${cell.column.getSize()}px` }}>
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ))}
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={columns.length} className="p-4 text-center">
                                        No results
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="mt-3 flex flex-row items-center justify-between text-sm">
                    <div>
                        <strong>{contentGroupEntityCollection?.total ?? 0}</strong>
                        {" "}
                        Items
                    </div>

                    <div className="flex flex-row items-center gap-3">
                        <button
                            className="px-3 py-1 disabled:text-gray-600"
                            onClick={() => setPagination((prevPagination) => {
                                return { ...prevPagination, pageIndex: 0 };
                            })}
                            disabled={(!contentGroupEntityCollection?.total) || (pagination.pageIndex === 0)}
                        >
                            <FontAwesomeIcon icon={faChevronLeft} size="1x" />
                            <FontAwesomeIcon icon={faChevronLeft} size="1x" />
                        </button>

                        <button
                            className="px-3 py-1 disabled:text-gray-600"
                            onClick={() =>
                                setPagination((prevPagination) => {
                                    const prevPageIndex = prevPagination.pageIndex;
                                    return { ...prevPagination, pageIndex: Math.max(0, prevPageIndex - 1) };
                                })
                            }
                            disabled={(!contentGroupEntityCollection?.total) || (pagination.pageIndex === 0)}
                        >
                            <FontAwesomeIcon icon={faChevronLeft} size="1x" />
                        </button>

                        <span className="text-sm">
                            Page
                            {" "}
                            <strong>{pagination.pageIndex + 1}</strong>
                            {" "}
                            of
                            {" "}
                            <strong>{Math.max(1, Math.ceil((contentGroupEntityCollection?.total ?? 0) / pagination.pageSize))}</strong>
                        </span>

                        <button
                            className="px-3 py-1 disabled:text-gray-600"
                            onClick={() =>
                                setPagination((prevPagination) => {
                                    const total = contentGroupEntityCollection?.total ?? 0;
                                    const prevPageIndex = prevPagination.pageIndex;
                                    const pageSize = prevPagination.pageSize;
                                    return { ...prevPagination, pageIndex: (prevPageIndex + 1 < Math.ceil(total / pageSize) ? prevPageIndex + 1 : prevPageIndex) };
                                })
                            }
                            disabled={(!contentGroupEntityCollection?.total) || ((pagination.pageIndex + 1) * pagination.pageSize >= contentGroupEntityCollection.total)}
                        >
                            <FontAwesomeIcon icon={faChevronRight} size="1x" />
                        </button>

                        <button
                            className="px-3 py-1 disabled:text-gray-600"
                            onClick={() =>
                                setPagination((prevPagination) => {
                                    const total = contentGroupEntityCollection?.total ?? 0;
                                    const pageSize = prevPagination.pageSize;
                                    return { ...prevPagination, pageIndex: (Math.ceil(total / pageSize) - 1) };
                                })
                            }
                            disabled={(!contentGroupEntityCollection?.total) || ((pagination.pageIndex + 1) * pagination.pageSize >= contentGroupEntityCollection.total)}
                        >
                            <FontAwesomeIcon icon={faChevronRight} size="1x" />
                            <FontAwesomeIcon icon={faChevronRight} size="1x" />
                        </button>
                    </div>

                    <div className="flex flex-row items-center gap-2">
                        <label>Page Size</label>
                        <select
                            value={pagination.pageSize}
                            onChange={(e) =>
                                setPagination({
                                    pageIndex: 0,
                                    pageSize: Number(e.target.value),
                                })
                            }
                            className="border px-2 py-1 rounded bg-light dark:bg-brand-700"
                        >
                            <option className={pagination.pageSize === 10 ? "bg-brand-400" : "bg-light dark:bg-brand-700"} value={10}>10</option>
                            <option className={pagination.pageSize === 20 ? "bg-brand-400" : "bg-light dark:bg-brand-700"} value={20}>20</option>
                            <option className={pagination.pageSize === 50 ? "bg-brand-400" : "bg-light dark:bg-brand-700"} value={50}>50</option>
                            <option className={pagination.pageSize === 100 ? "bg-brand-400" : "bg-light dark:bg-brand-700"} value={100}>100</option>
                        </select>
                    </div>
                </div>
            </div>
            {
                selectedRow &&
                <>
                    <ContentGroupModal
                        contentGroup={selectedRow}
                        modalId={"edit-vod-content-group-main-metadata-listing"}
                    />
                    {
                        selectedRow && selectedRow.id &&
                        <ExportModal
                            mutateFunc={exportUpdateVodContentGroup.mutate}
                            title ={`${selectedRow.id} - ${selectedRow.title}`}
                            id={selectedRow.id}
                            modalId={"export-update-vod-content-group-listing"}
                        />
                    }
                </>
            }
        </>
    );
};

export default VODSeriesContentGroupListingView;